import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  Image, ActivityIndicator, Alert, ScrollView,
} from 'react-native';
import { useState, useEffect } from 'react';
import * as ImagePicker from 'expo-image-picker';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import { compressImageForUpload } from '@/lib/image-utils';

export default function StudentMyProfileScreen() {
  const { profile } = useAuth();
  const [nickname, setNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [studentId, setStudentId] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setNickname(profile.nickname ?? '');
    setAvatarUrl(profile.avatar_url ?? null);
    supabase
      .from('students')
      .select('id, phone')
      .eq('profile_id', profile.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setStudentId(data.id);
          setPhone(data.phone ?? '');
        }
      });
  }, [profile]);

  async function handlePickAvatar() {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== 'granted') { Alert.alert('Permissão necessária'); return; }
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      // quality 1 pra picker — compressao real acontece em compressImageForUpload abaixo
      quality: 1,
      allowsEditing: true,
      aspect: [1, 1],
    });
    if (result.canceled || !result.assets[0]) return;
    setSaving(true);
    try {
      // Resize 800x800 + JPEG 0.85: ~1.9 MB → ~150 KB (AÇ-014)
      const compressed = await compressImageForUpload(result.assets[0].uri, {
        maxWidth: 800,
        quality: 0.85,
      });
      // Output sempre JPEG agora — extensao fixa pra evitar legado .png/.gif residual
      const path = `${profile!.id}.jpg`;
      const response = await fetch(compressed.uri);
      const blob = await response.blob();
      const { error } = await supabase.storage
        .from('avatars')
        .upload(path, blob, { upsert: true, contentType: 'image/jpeg' });
      if (error) throw error;
      const { data } = supabase.storage.from('avatars').getPublicUrl(path);
      setAvatarUrl(data.publicUrl);
      await supabase.from('profiles').update({ avatar_url: data.publicUrl }).eq('id', profile!.id);
    } catch (e: any) {
      Alert.alert('Erro ao enviar foto', e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    if (!profile) return;
    setSaving(true);
    await Promise.all([
      supabase.from('profiles').update({ nickname: nickname.trim() || null }).eq('id', profile.id),
      studentId
        ? supabase.from('students').update({ phone: phone.trim() || null }).eq('id', studentId)
        : Promise.resolve(),
    ]);
    setSaving(false);
    Alert.alert('Salvo!', 'Seu perfil foi atualizado.');
  }

  if (!profile) return <ActivityIndicator style={{ flex: 1 }} color="#C8102E" />;

  const initials = profile.full_name
    .split(' ')
    .map((w: string) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.content}
      keyboardShouldPersistTaps="handled"
    >
      <TouchableOpacity onPress={handlePickAvatar} style={styles.avatarWrap} disabled={saving}>
        {avatarUrl
          ? <Image source={{ uri: avatarUrl }} style={styles.avatarImg} />
          : (
            <View style={styles.avatarCircle}>
              <Text style={styles.avatarInitials}>{initials}</Text>
            </View>
          )
        }
        <View style={styles.cameraBtn}>
          <Text style={styles.cameraBtnText}>📷</Text>
        </View>
      </TouchableOpacity>

      <Text style={styles.label}>Como gostaria de ser chamado</Text>
      <TextInput
        style={styles.input}
        value={nickname}
        onChangeText={setNickname}
        placeholder="Ex: Mari, Rafa..."
        placeholderTextColor="#bbb"
        returnKeyType="next"
      />

      <Text style={styles.label}>Telefone</Text>
      <TextInput
        style={styles.input}
        value={phone}
        onChangeText={setPhone}
        placeholder="(11) 99999-9999"
        placeholderTextColor="#bbb"
        keyboardType="phone-pad"
        returnKeyType="done"
      />

      <TouchableOpacity
        style={[styles.saveBtn, saving && styles.saveBtnDisabled]}
        onPress={handleSave}
        disabled={saving}
      >
        <Text style={styles.saveBtnText}>{saving ? 'Salvando...' : 'Salvar'}</Text>
      </TouchableOpacity>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F5F5F5' },
  content: { alignItems: 'center', paddingTop: 40, paddingBottom: 48, paddingHorizontal: 24 },
  avatarWrap: { position: 'relative', marginBottom: 36 },
  avatarImg: { width: 96, height: 96, borderRadius: 48 },
  avatarCircle: {
    width: 96, height: 96, borderRadius: 48,
    backgroundColor: '#C8102E22', alignItems: 'center', justifyContent: 'center',
  },
  avatarInitials: { fontSize: 34, fontWeight: '700', color: '#C8102E' },
  cameraBtn: {
    position: 'absolute', bottom: 0, right: 0,
    backgroundColor: '#C8102E', borderRadius: 15, width: 30, height: 30,
    alignItems: 'center', justifyContent: 'center',
    borderWidth: 2, borderColor: '#F5F5F5',
  },
  cameraBtnText: { fontSize: 14 },
  label: { alignSelf: 'flex-start', fontSize: 13, fontWeight: '600', color: '#555', marginBottom: 6 },
  input: {
    width: '100%', backgroundColor: '#fff', borderRadius: 10,
    padding: 14, fontSize: 15, borderWidth: 1, borderColor: '#eee',
    marginBottom: 20, color: '#1A1A1A',
  },
  saveBtn: {
    backgroundColor: '#C8102E', borderRadius: 12, padding: 16,
    width: '100%', alignItems: 'center', marginTop: 4,
  },
  saveBtnDisabled: { opacity: 0.5 },
  saveBtnText: { color: '#fff', fontWeight: 'bold', fontSize: 16 },
});
